// Home = tenant / outlet picker. Lists the user's memberships from
// GET /v1/me and routes to the per-outlet menu page.

import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiFetch } from '../api/client.js';
import type { MeResponse } from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { NotificationBell } from './components/NotificationBell.js';
import { TillDownloadCard } from './components/TillDownloadCard.js';

export function HomePage(): JSX.Element {
  const { user, logout } = useAuth();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<MeResponse>('/me'),
    staleTime: 30_000,
  });

  // One bell per distinct organization — a user with memberships in
  // two orgs sees two bells (one in each org's row). Server scopes
  // notifications per org, so a per-org bell is the honest shape.
  const distinctOrgIds = Array.from(
    new Set((me.data?.memberships ?? []).map((m) => m.organization.id)),
  );

  // The till installer is for the people who set a till up: owners and managers (ADR 0108).
  const canDownloadTill = (me.data?.memberships ?? []).some(
    (m) => m.role === 'organization_owner' || m.role === 'outlet_manager',
  );

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Your outlets</h1>
          <p className="muted">
            Signed in as <strong>{user?.email}</strong>
          </p>
        </div>
        <div className="header-actions">
          {distinctOrgIds.map((orgId) => (
            <NotificationBell key={orgId} orgId={orgId} />
          ))}
          {(me.data?.memberships ?? []).some((m) => m.permissions.includes('ops:read')) ? (
            <Link className="button secondary" to="/ops">
              Operator
            </Link>
          ) : null}
          <button type="button" onClick={logout}>
            Sign out
          </button>
        </div>
      </header>
      {canDownloadTill ? <TillDownloadCard /> : null}
      {me.isLoading ? <p>Loading…</p> : null}
      {me.error ? <p className="error">Could not load memberships.</p> : null}
      {me.data ? (
        me.data.memberships.length === 0 ? (
          <p className="muted">You have no outlet access yet. Ask an owner to add you.</p>
        ) : (
          <ul className="outlet-list">
            {me.data.memberships.map((m) => (
              <li key={`${m.organization.id}:${m.outlet.id}`} className="card outlet-row">
                <div>
                  <h2>{m.outlet.name}</h2>
                  <p className="muted">
                    {m.restaurant.name} · {m.organization.name} · {m.role}
                  </p>
                </div>
                <div className="row-actions">
                  <Link
                    className="button"
                    to={`/orgs/${m.organization.id}/outlets/${m.outlet.id}/menus`}
                  >
                    Open menus
                  </Link>
                  <Link
                    className="button secondary"
                    to={`/orgs/${m.organization.id}/outlets/${m.outlet.id}/feedback`}
                  >
                    Feedback
                  </Link>
                  <Link
                    className="button secondary"
                    to={`/orgs/${m.organization.id}/restaurants/${m.restaurant.id}/loyalty?outletId=${m.outlet.id}`}
                  >
                    Loyalty
                  </Link>
                  <Link
                    className="button secondary"
                    to={`/orgs/${m.organization.id}/outlets/${m.outlet.id}/analytics`}
                  >
                    Analytics
                  </Link>
                  {m.permissions.includes('pos:read') &&
                  m.outlet.servicePackage !== 'digital_menu' ? (
                    <Link
                      className="button secondary"
                      to={`/orgs/${m.organization.id}/outlets/${m.outlet.id}/pos`}
                    >
                      Till &amp; sales
                    </Link>
                  ) : null}
                  {m.outlet.servicePackage !== 'digital_menu' ? (
                    <Link
                      className="button secondary"
                      to={`/orgs/${m.organization.id}/outlets/${m.outlet.id}/tables`}
                    >
                      Tables &amp; QR
                    </Link>
                  ) : null}
                  {m.permissions.includes('pos:read') &&
                  m.outlet.servicePackage !== 'digital_menu' ? (
                    <Link
                      className="button secondary"
                      to={`/orgs/${m.organization.id}/outlets/${m.outlet.id}/guest-orders`}
                    >
                      Guest orders
                    </Link>
                  ) : null}
                  <Link
                    className="button secondary"
                    to={`/orgs/${m.organization.id}/outlets/${m.outlet.id}/settings`}
                  >
                    Settings
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </main>
  );
}
